package com.evoapps.evoshift.data.remote

import io.ktor.client.HttpClient
import io.ktor.client.request.header
import io.ktor.client.request.post
import io.ktor.client.request.setBody
import io.ktor.client.statement.bodyAsText
import io.ktor.http.ContentType
import io.ktor.http.HttpHeaders
import io.ktor.http.contentType
import io.ktor.http.isSuccess
import kotlinx.serialization.json.Json
import kotlinx.serialization.json.JsonElement
import kotlinx.serialization.json.JsonNull
import kotlinx.serialization.json.JsonObject
import kotlinx.serialization.json.buildJsonObject
import kotlinx.serialization.json.contentOrNull
import kotlinx.serialization.json.jsonObject
import kotlinx.serialization.json.jsonPrimitive
import kotlinx.serialization.json.put

/** A callable Cloud Function failed; [code] is the `functions.https` error code, e.g. `not-found`. */
class CloudFunctionsException(
    val code: String,
    message: String,
    /** The HttpsError's `details` (e.g. `{"reason": "QUOTA_EXCEEDED"}`), when the function sent any. */
    val details: JsonElement? = null
) : Exception(message)

/**
 * Calls Firebase callable Cloud Functions over plain HTTPS (the `{ "data": ... }` /
 * `{ "result": ... }` protocol) with the signed-in user's ID token, so every platform uses the
 * same code without a platform Functions SDK. Functions live in `functions/index.js`.
 * Where the platform installed App Check (SH-185), its token goes along in [AppCheckTokens.HEADER];
 * the functions only log it (functions/appCheck.js), nothing is enforced.
 */
class CloudFunctionsClient(
    private val httpClient: HttpClient,
    private val baseUrl: String,
    private val appCheckToken: suspend () -> String? = { AppCheckTokens.current() },
    private val idToken: suspend () -> String?
) {
    private val json = Json { ignoreUnknownKeys = true }

    /** Calls [name]; with [requireSignIn] = false also signed out (e.g. "forgot password"). */
    suspend fun call(name: String, data: JsonObject, requireSignIn: Boolean = true): JsonElement {
        val token = idToken()
        if (token == null && requireSignIn) throw CloudFunctionsException("unauthenticated", "You must be signed in.")
        val appCheck = appCheckToken()
        val response = httpClient.post("$baseUrl/$name") {
            if (token != null) header(HttpHeaders.Authorization, "Bearer $token")
            if (appCheck != null) header(AppCheckTokens.HEADER, appCheck)
            contentType(ContentType.Application.Json)
            setBody(buildJsonObject { put("data", data) }.toString())
        }
        val body = runCatching { json.parseToJsonElement(response.bodyAsText()).jsonObject }.getOrNull()
        body?.get("error")?.jsonObject?.let { error ->
            val status = error["status"]?.jsonPrimitive?.contentOrNull ?: "UNKNOWN"
            val message = error["message"]?.jsonPrimitive?.contentOrNull ?: "Cloud Function $name failed"
            throw CloudFunctionsException(status.lowercase().replace('_', '-'), message, error["details"]?.takeIf { it !is JsonNull })
        }
        if (!response.status.isSuccess()) {
            throw CloudFunctionsException("internal", "Cloud Function $name failed with HTTP ${response.status.value}")
        }
        return body?.get("result") ?: JsonNull
    }

    companion object {
        fun baseUrl(projectId: String, region: String) = "https://$region-$projectId.cloudfunctions.net"
    }
}
